import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import RegionEditor from "./RegionEditor";
import { useStudio } from "./StudioContext";

const StudioChrome = () => {
  const location = useLocation();
  const {
    enabled,
    studioMode,
    enterStudio,
    exitStudio,
    resetFromCode,
    status,
    apiOnline,
    applying,
    dirty,
    saveLocal,
    saveAndApply,
  } = useStudio();

  useEffect(() => {
    if (!studioMode) return undefined;
    document.body.classList.add("studio-visual-mode");
    return () => document.body.classList.remove("studio-visual-mode");
  }, [studioMode]);

  if (!enabled) return null;
  if (location.pathname.startsWith("/admin")) return null;

  if (!studioMode) {
    return (
      <button
        type="button"
        onClick={enterStudio}
        className="fixed bottom-4 right-4 z-[60] rounded-full bg-green-solid px-3 py-2 text-sm font-semibold text-white shadow-lg hover:brightness-110"
        title="Edit this page visually (dev only)"
      >
        ✎ Visual edit
      </button>
    );
  }

  return (
    <>
      <div className="fixed inset-x-0 top-0 z-[60] flex items-center gap-3 border-b border-green-solid/40 bg-green-solid px-3 py-2 text-white shadow">
        <span className="text-sm font-semibold uppercase tracking-wide">
          Visual Studio
        </span>
        <span className="hidden text-[11px] text-white/90 sm:inline">
          Click a dashed region to edit • {dirty ? "Unsaved changes" : "In sync"}
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Link
            to="/admin"
            className="rounded bg-white/15 px-2 py-1 text-[11px] font-medium hover:bg-white/25"
          >
            Full Studio
          </Link>
          <button
            type="button"
            onClick={saveLocal}
            className="rounded bg-white/15 px-2 py-1 text-[11px] font-medium hover:bg-white/25"
          >
            Save draft
          </button>
          <button
            type="button"
            onClick={saveAndApply}
            disabled={applying || !apiOnline}
            className="rounded bg-white px-2 py-1 text-[11px] font-semibold text-green-solid disabled:opacity-50"
            title={
              apiOnline
                ? "Write draft.json and sync src/data"
                : "Content API offline"
            }
          >
            {applying ? "Applying…" : "Save & apply"}
          </button>
          <button
            type="button"
            onClick={resetFromCode}
            className="rounded bg-white/15 px-2 py-1 text-[11px] font-medium hover:bg-white/25"
            title="Discard browser draft and reload content from current src/data"
          >
            Reset from code
          </button>
          <button
            type="button"
            onClick={exitStudio}
            className="rounded bg-black/20 px-2 py-1 text-[11px] font-medium hover:bg-black/30"
          >
            Exit
          </button>
        </div>
      </div>

      <aside className="fixed bottom-0 right-0 top-10 z-[55] flex w-full max-w-md flex-col border-l border-gray-200 bg-lightBg shadow-xl dark:border-gray-700 dark:bg-darkBg sm:top-[2.35rem]">
        <div className="flex-1 overflow-y-auto p-4">
          {status && (
            <p className="mb-3 rounded-md bg-green/10 px-2 py-1.5 text-sm text-green dark:text-green-dark">
              {status}
            </p>
          )}
          <RegionEditor />
        </div>
        <div className="border-t border-gray-200 px-4 py-2 text-[10px] text-gray-500 dark:border-gray-700">
          Dev only • same draft as /admin • not shipped to production
        </div>
      </aside>
    </>
  );
};

export default StudioChrome;
